import ApeironComponent from '@/components/commons/apeiron-component.model';
import SolicitudService from '@/entities/solicitud/solicitud.service';
import AlertService from '@/shared/alert/alert.service';
import { AreaConocimiento, ICatalogoArea } from '@/shared/model/snp/campo-conocimiento.model';
import CatalogosSnp from '@/shared/model/snp/catalogos-snp.factory';
import { Solicitud } from '@/shared/model/solicitud.model';
import { SaciApi } from '@conacyt/saci-client';
import { BModal, BvTableFieldArray } from 'bootstrap-vue';
import { Component, Emit, Inject, Prop, Ref, Watch } from 'vue-property-decorator';
import { required, requiredIf } from 'vuelidate/lib/validators';

const validations = function () {
  const { areaConocimiento } = this as CamposConocimientoComponent;
  return {
    areaConocimiento: {
      area: { required },
      campo: { required },
      disciplina: { required },
      otraDisciplina: {
        required: requiredIf(
          () =>
            (areaConocimiento.disciplina?.nombre ?? '').startsWith('Otra') || (areaConocimiento.disciplina?.nombre ?? '').startsWith('Otro')
        ),
      },
      subdisciplina: { required },
      otraSubdisciplina: {
        required: requiredIf(
          () =>
            (areaConocimiento.subdisciplina?.nombre ?? '').startsWith('Otras') ||
            (areaConocimiento.subdisciplina?.nombre ?? '').startsWith('Otros')
        ),
      },
    },
  };
};

@Component({ validations })
export default class CamposConocimientoComponent extends ApeironComponent {
  @Inject('alertService') public alertService: () => AlertService;
  @Inject('saciApi') public saciApi: () => SaciApi;
  @Inject('solicitudService') public solicitudService: () => SolicitudService;

  @Prop({ required: true })
  public solicitud: Solicitud;

  public areaConocimiento: AreaConocimiento = AreaConocimiento.fromObject(new AreaConocimiento());

  public areas: Array<ICatalogoArea> = CatalogosSnp.obtenerAreasDeConocimiento();

  public catalogoArea: Array<AreaConocimiento> = [];
  public catalogoCampo: Array<AreaConocimiento> = [];
  public catalogoDisciplina: Array<AreaConocimiento> = [];
  public catalogoSubdisciplina: Array<AreaConocimiento> = [];

  public areasConocimiento: Array<AreaConocimiento> = [];

  public page = 1;
  public registros = 100;
  public mostrarSeccionRegistro = false;
  public mostrarLista = true;

  public areaAEliminar: null;

  @Ref('confirmarModalEliminar')
  public readonly confirmarModalEliminar: BModal;

  @Ref('confirmarModalPrincipal')
  public readonly confirmarModalPrincipal: BModal;

  @Ref('confirmarModalNoPrincipal')
  public readonly confirmarModalNoPrincipal: BModal;

  public readonly fieldsSolicitudes: BvTableFieldArray = [
    {
      key: 'area',
      label: this.$t('apeironGwApp.programa.camposConocimiento.areaConocimiento').toString(),
      class: 'text-center',
      sortable: true,
    },
    {
      key: 'campo',
      label: this.$t('apeironGwApp.programa.camposConocimiento.campo').toString(),
      class: 'text-center',
      sortable: true,
    },
    {
      key: 'disciplina',
      label: this.$t('apeironGwApp.programa.camposConocimiento.disciplina').toString(),
      class: 'text-center',
      sortable: true,
    },
    {
      key: 'subDisciplina',
      label: this.$t('apeironGwApp.programa.camposConocimiento.subDisciplina').toString(),
      class: 'text-center',
      sortable: true,
    },
    { key: 'esPrincipal', label: this.$t('apeironGwApp.programa.camposConocimiento.principal').toString(), class: 'text-center' },
    {
      key: 'accion',
      label: this.$t('bandeja.acciones').toString(),
      class: 'text-center',
      thClass: this.esEscritura ? '' : 'd-none',
      tdClass: this.esEscritura ? '' : 'd-none',
    },
  ];

  mounted() {
    this.get();
  }

  @Watch('mostrarSeccionRegistro')
  private watchMostrarSeccionRegistro(): void {
    if (this.mostrarSeccionRegistro) {
      this.limpiarAreaConocimiento();
    }
  }

  /**
   * Carga inicial del componente
   *
   * @memberof CamposConocimientoComponent
   */
  public get(): void {
    if (this.solicitud?.areasConocimiento) {
      this.areasConocimiento = this.solicitud.areasConocimiento;
    }
  }

  /**
   * Obtiene los campos de conocimiento de un área específica y actualiza el estado de los campos de lectura.
   *
   * @memberof CamposConocimientoComponent
   */
  public obtenerCampos(area: AreaConocimiento): void {
    if (area) {
      this.getCatalogos('getCatCamposAreasConocimiento', area.id)
        .then(res => {
          this.catalogoCampo = res;
        })
        .finally(() => {
          this.areaConocimiento.campo = undefined;
          this.areaConocimiento.disciplina = undefined;
          this.areaConocimiento.otraDisciplina = undefined;
          this.areaConocimiento.subdisciplina = undefined;
          this.areaConocimiento.otraSubdisciplina = undefined;
        });
    }
  }

  /**
   * Obtiene las disciplinas de conocimiento de un campo específico y actualiza el estado de las disciplinas de lectura.
   *
   * @memberof CamposConocimientoComponent
   */
  public obtenerDisciplinas(campo: AreaConocimiento): void {
    if (campo) {
      this.getCatalogos('getCatDisciplinasCampos', campo.id)
        .then(res => {
          this.catalogoDisciplina = res;
        })
        .finally(() => {
          this.areaConocimiento.disciplina = undefined;
          this.areaConocimiento.otraDisciplina = undefined;
          this.areaConocimiento.subdisciplina = undefined;
          this.areaConocimiento.otraSubdisciplina = undefined;
        });
    }
  }

  /**
   * Obtiene las subdDsciplinas de conocimiento de una discilpina específico y actualiza el estado de las subdDsciplinas de lectura.
   *
   * @memberof CamposConocimientoComponent
   */
  public obtenerSubDisciplinas(disciplina: AreaConocimiento): void {
    if (disciplina) {
      this.getCatalogos('getSubDisciplinasDisciplinas', disciplina.id)
        .then(res => {
          this.catalogoSubdisciplina = res;
        })
        .finally(() => {
          this.areaConocimiento.subdisciplina = undefined;
          this.areaConocimiento.otraSubdisciplina = undefined;
        });
    }
  }

  /**
   * Obtiene los datos del área de conocimiento desde el catálogo especificado SACI.
   *
   * @memberof CamposConocimientoComponent
   */
  public getCatalogos(catalogo: string, param1): Promise<any[]> {
    return new Promise(resolve => {
      try {
        this.saciApi()
          [catalogo](param1, this.page, this.registros)
          .then(res => {
            resolve(this.toCatalogoVue(res.data));
          })
          .catch(error => {
            this.alertService().showHttpError(this, error.response);
          });
      } catch (error) {
        this.alertService().showHttpError(this, error.response);
      }
    });
  }

  /**
   * Transforma un array de objetos JSON en un formato específico para ser usado en un catálogo Vue.
   *
   * @memberof CamposConocimientoComponent
   */
  public toCatalogoVue(jsonData: Array<any>): Array<any> {
    const catalogo = [];
    jsonData.forEach(element => {
      catalogo.push({ value: element, text: element.nombre });
    });
    return catalogo;
  }

  /**
   * Elimina un área de conocimiento de la lista de áreas de conocimiento.
   *
   * @memberof CamposConocimientoComponent
   */
  public eliminarAreaConocimiento(areaConocimiento: AreaConocimiento) {
    this.areasConocimiento = this.areasConocimiento.filter(area => area !== areaConocimiento);
    this.guardarAreaConocimiento();
  }

  /**
   * Guarda el área de conocimiento en la solicitud actual y actualiza la solicitud.
   *
   * @memberof CamposConocimientoComponent
   */
  public guardarSeccion(): void {
    const tienePrincipal = this.areasConocimiento.some(area => area.esPrincipal === true);

    if (tienePrincipal) {
      this.guardarAreaConocimiento();
    } else {
      this.alertService().showWarning(this, this.$t('apeironGwApp.programa.camposConocimiento.campoPrincipal', 'Falla').toString());
    }
  }

  /**
   * Este método `limpiarAreaConocimiento` restablece el estado de las propiedades
   * relacionadas con el área de conocimiento a sus valores predeterminados.
   *
   * @memberof CamposConocimientoComponent
   */
  private limpiarAreaConocimiento() {
    this.catalogoCampo = [];
    this.catalogoDisciplina = [];
    this.catalogoSubdisciplina = [];
    this.areaConocimiento = AreaConocimiento.fromObject(new AreaConocimiento());
    this.areaConocimiento.campo = undefined;
    this.areaConocimiento.disciplina = undefined;
    this.areaConocimiento.otraDisciplina = undefined;
    this.areaConocimiento.subdisciplina = undefined;
    this.areaConocimiento.otraSubdisciplina = undefined;
  }

  /**
   * Transforma un array de objetos AreaConocimiento en un array de objetos con claves y nombres específicos.
   *
   * @memberof CamposConocimientoComponent
   */
  public transformAreasConocimiento(areas: Array<AreaConocimiento>): Array<any> {
    return areas.map(areaConocimiento => ({
      cveArea: `0${areaConocimiento.area?.clave}`,
      area: areaConocimiento.area?.nombre,
      cveCampo: areaConocimiento.campo?.clave,
      campo: areaConocimiento.campo?.nombre,
      cveDisciplina: areaConocimiento.disciplina?.clave,
      disciplina: areaConocimiento.disciplina?.nombre,
      cveSubDisciplina: areaConocimiento.subdisciplina?.clave,
      subDisciplina: areaConocimiento.subdisciplina?.nombre,
      esPrincipal: areaConocimiento?.esPrincipal ?? false,
      otraDisciplina: areaConocimiento?.otraDisciplina,
      otraSubDisciplina: areaConocimiento?.otraSubdisciplina,
    }));
  }

  /**
   * Agrega un nuevo objeto de área de conocimiento a la lista de áreas de conocimiento.
   *
   * @memberof CamposConocimientoComponent
   */
  public agregarArea() {
    if (
      this.areaConocimiento.area.nombre &&
      this.areaConocimiento.campo.nombre &&
      this.areaConocimiento.disciplina.nombre &&
      this.areaConocimiento.subdisciplina.nombre
    ) {
      const nuevaArea = AreaConocimiento.fromObject(this.areaConocimiento);
      const areaTransformada = this.transformAreasConocimiento([nuevaArea])[0];
      this.areasConocimiento.push(areaTransformada);
      this.limpiarAreaConocimiento();
      this.mostrarLista = true;
    } else {
      this.alertService().showWarning(this, this.$t('apeironGwApp.programa.camposConocimiento.camposRequeridos', 'Falla').toString());
    }
  }

  /**
   * Muestra un modal de confirmación para eliminar un área de conocimiento.
   *
   * @memberof CamposConocimientoComponent
   */
  public mostrarModalConfirmacionEliminarArea(areaConocimiento: AreaConocimiento): void {
    this.areaConocimiento = areaConocimiento;
    this.confirmarModalEliminar.show();
  }

  /**
   * Método para confirmar la acción de eliminación de un área de conocimiento.
   *
   * @memberof CamposConocimientoComponent
   */
  public confirmarAccionEliminarArea(): void {
    this.eliminarAreaConocimiento(this.areaConocimiento);

    this.confirmarModalEliminar.hide();
  }

  /**
   * Oculta el modal de eliminar un área de conocimiento.
   *
   * @memberof CamposConocimientoComponent
   */
  public ocultarModalEliminarArea(): void {
    this.confirmarModalEliminar.hide();
  }

  /**
   * Muestra un modal de confirmación para designar un área de conocimiento como principal.
   *
   * @memberof CamposConocimientoComponent
   */
  public mostrarModalConfirmacionAreaPrincipal(areaConocimientoSeleccionada: AreaConocimiento): void {
    this.areaConocimiento = areaConocimientoSeleccionada;
    this.confirmarModalPrincipal.show();
  }

  /**
   * Muestra el modal para quitar el área principal.
   * @param areaConocimientoSeleccionada área
   */
  public mostrarModalQuitarAreaPrincipal(areaConocimientoSeleccionada: AreaConocimiento): void {
    this.areaConocimiento = areaConocimientoSeleccionada;
    this.confirmarModalNoPrincipal.show();
  }

  /**
   * Método para confirmar la acción de agregar un área de conocimiento como principal.
   *
   * @memberof CamposConocimientoComponent
   */
  public confirmarAccionAreaPrincipal(): void {
    this.confirmarModalPrincipal.hide();
    (this.areasConocimiento ?? []).filter(area => area.esPrincipal).forEach(area => (area.esPrincipal = false));
    this.areaConocimiento.esPrincipal = true;
  }

  public confirmarAccionQuitarAreaPrincipal(): void {
    this.confirmarModalNoPrincipal.hide();
    this.areaConocimiento.esPrincipal = false;
  }

  /**
   * Oculta el modal para agregar un área de conocimiento como principal
   * y muestra las areas conocimiento que contiene la solicitud originalmente.
   *
   * @memberof CamposConocimientoComponent
   */
  public ocultarModalAreaPrincipal(): void {
    this.areaConocimiento = AreaConocimiento.fromObject(new AreaConocimiento());
    this.confirmarModalPrincipal.hide();
    this.confirmarModalNoPrincipal.hide();
  }

  get activarBoton(): boolean {
    return this.$v.$invalid;
  }

  get mostrarTexto() {
    return this.solicitud?.areasConocimiento?.length === 0 || !this.solicitud?.areasConocimiento;
  }

  @Emit('guardarAreaConocimiento')
  public guardarAreaConocimiento(): { areasConocimiento: Array<AreaConocimiento> } {
    return { areasConocimiento: this.areasConocimiento };
  }

  /**
   * Indicador para permitir el modo escritura
   */
  get esEscritura(): boolean {
    return this.esEscrituraConEstadoEnCaptura(this.solicitud.estado);
  }
}
